import React, { useState } from 'react';

import { gql } from '@apollo/client';
import ErrorBoundary from 'components/ErrorBoundary';
import useCustomer from 'hooks/useCustomer';
import { useQuery } from 'hooks/apollo';
import { combineQueries, getBaseQuery, getDateQuery } from 'shared/utils/queryBuilder';
import { BarChartOutlined, TableOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import messages from 'components/Charts/messages';

import { Radio, Tooltip } from 'antd';
import ResultStats from './ResultStats';
import ChartResult from './ChartResult';
import TableResult from './TableResult';

const RESULT_QUERY = gql`
  query CustomResult($input: ResultsInput!) {
    results(input: $input) {
      segmentId
      data
      totals
      statistics {
        elapsed
        rowsRead
        bytesRead
        rows
        rowsBeforeLimitAtLeast
        transferred
      }
    }
  }
`;

const PresentationTypeSwitcher = ({ presentationType, setPresentationType }) => (
  <Radio.Group buttonStyle="solid" value={presentationType} onChange={e => setPresentationType(e.target.value)}>
    <Tooltip key="table" placement="topLeft" title={<FormattedMessage {...messages.table} />}>
      <Radio.Button value="table">
        <TableOutlined />
      </Radio.Button>
    </Tooltip>
    <Tooltip key="bar" placement="topLeft" title={<FormattedMessage {...messages.bar} />}>
      <Radio.Button value="bar">
        <BarChartOutlined />
      </Radio.Button>
    </Tooltip>
    {/* <Tooltip key="pie" placement="topLeft" title={<FormattedMessage {...messages.pie} />}>
      <Radio.Button value="pie">
        <PieChartOutlined />
      </Radio.Button>
    </Tooltip>
    <Tooltip key="line" placement="topLeft" title={<FormattedMessage {...messages.line} />}>
      <Radio.Button value="line">
        <LineChartOutlined />
      </Radio.Button>
    </Tooltip>
    <Tooltip key="area" placement="topLeft" title={<FormattedMessage {...messages.area} />}>
      <Radio.Button value="area">
        <AreaChartOutlined />
      </Radio.Button>
    </Tooltip>
    <Tooltip key="stacked" placement="topLeft" title={<FormattedMessage {...messages.stacked} />}>
      <Radio.Button value="stacked">
        <StackedBarChartIcon />
      </Radio.Button>
    </Tooltip>
    <Tooltip key="scatter" placement="topLeft" title={<FormattedMessage {...messages.scatter} />}>
      <Radio.Button value="scatter">
        <DotChartOutlined />
      </Radio.Button>
    </Tooltip> */}
  </Radio.Group>
);

const getQueryInput = ({ customerId, resultConfig, globalFilter, dataSourceIds }) => ({
  input: {
    metrics: resultConfig.metrics?.filter(m => m.field).map(m => ({ name: m.name, field: m.field, aggregation: m.aggregation })),
    dimensions: resultConfig.dimensions?.map(d => ({ name: d.name, field: d.field, transforms: d.transforms, excludeEmptyRows: d.excludeEmptyRows, limitBy: d.limitBy })),
    baseQuery: combineQueries(getBaseQuery({ customerId, dataSourceIds }), resultConfig.filter),
    mainSegment: globalFilter.mainSegment ? {
      id: globalFilter.mainSegment.id,
      query: globalFilter.mainSegment.query,
      dateQuery: getDateQuery({ dateRangeConfig: globalFilter.mainSegment.dateRangeConfig || globalFilter.mainDateRangeConfig, globalDateRangeConfig: globalFilter.mainDateRangeConfig }),
    } : null,
    comparisonSegments: globalFilter.comparisonSegments?.filter(s => s.enabled).map(s => ({
      id: s.id,
      query: s.query,
      dateQuery: getDateQuery({ dateRangeConfig: s.dateRangeConfig, globalDateRangeConfig: globalFilter.mainDateRangeConfig }),
    })),
    rowLimit: resultConfig.settings?.rowLimit,
  },
});

const SegmentsResult = ({ resultConfig, globalFilter, dataSourceIds, showStats, width }) => {
  const [presentationType, setPresentationType] = useState('table');
  const { customerId } = useCustomer();
  const variables = customerId && resultConfig && globalFilter ? getQueryInput({ customerId, dataSourceIds, resultConfig, globalFilter }) : null;
  const { data, loading } = useQuery(RESULT_QUERY, {
    variables,
    skip: !variables,
  });

  return (
    <ErrorBoundary reset={data?.results}>
      {presentationType === 'table' && <TableResult results={data?.results} loading={loading} resultConfig={resultConfig} globalFilter={globalFilter} width={width} />}
      {presentationType !== 'table' && <ChartResult results={data?.results} loading={loading} resultConfig={resultConfig} globalFilter={globalFilter} presentationType={presentationType} />}
      <div style={{ padding: '16px' }}>
        <PresentationTypeSwitcher presentationType={presentationType} setPresentationType={setPresentationType} />
      </div>
      {showStats && <ResultStats results={data?.results} loading={loading} globalFilter={globalFilter} />}
    </ErrorBoundary>

  );
};

export default SegmentsResult;
